/**
 * WP-H2 / WP-H2.1 / WP-C1 — Hospitol durable SoT helpers.
 * When Hospitol API reports DATABASE_URL configured, API/DB is source of truth;
 * localStorage is cache only (migrate local → API when remote empty; never silent discard).
 * Admissions: Care durable (v1.1.0). Pharmacy edition still denies /admissions API.
 */
import { getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { HOSPITOL_API_BASE_URL } from "@/core/config";
import { getStoredMeta, loadStoredSession } from "@itaas/core/auth/auth.storage";

const DURABLE_PROBE_KEY = "hospitol.pharmacy.durableProbe";

export type PharmacySotDomain =
  | "patients"
  | "prescriptions"
  | "invoices"
  | "admissions"
  | "consultations"
  | "billingDocuments";

let durableConfiguredCache: boolean | null = null;

export type HospitolHealthSnapshot = {
  status?: string;
  durablePersistenceConfigured?: boolean;
};

function sotMigratedKey(domain: PharmacySotDomain): string {
  return `hospitol.pharmacy.sotMigrated.${domain}`;
}

export async function probeHospitolDurableConfigured(): Promise<boolean> {
  if (durableConfiguredCache !== null) return durableConfiguredCache;
  try {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "X-Tenant-Slug": loadStoredSession()?.tenant?.slug ?? "demo-hospitol",
    };
    const { accessToken } = getStoredMeta();
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    const response = await fetch(`${HOSPITOL_API_BASE_URL}/health`, { headers });
    if (!response.ok) {
      durableConfiguredCache = false;
      return false;
    }
    const body = (await response.json()) as HospitolHealthSnapshot;
    durableConfiguredCache = Boolean(body.durablePersistenceConfigured);
    setStorageItem(DURABLE_PROBE_KEY, durableConfiguredCache ? "1" : "0");
    return durableConfiguredCache;
  } catch {
    const cached = getStorageItem(DURABLE_PROBE_KEY);
    durableConfiguredCache = cached === "1";
    return durableConfiguredCache;
  }
}

export function isPharmacySotMigrated(domain: PharmacySotDomain): boolean {
  return getStorageItem(sotMigratedKey(domain)) === "1";
}

export function markPharmacySotMigrated(domain: PharmacySotDomain): void {
  setStorageItem(sotMigratedKey(domain), "1");
}

/**
 * WP-H2.1 — only mark migrated after remote confirms durable rows exist.
 * Prevents migrate-flag=true with empty DB (silent LS wipe risk).
 */
export function markPharmacySotMigratedIfRemoteConfirmed(
  domain: PharmacySotDomain,
  durable: boolean,
  remoteCount: number,
): void {
  if (durable && remoteCount > 0) markPharmacySotMigrated(domain);
}

export function isPharmacyDurableSotActive(domain: PharmacySotDomain): boolean {
  return getStorageItem(DURABLE_PROBE_KEY) === "1" && isPharmacySotMigrated(domain);
}

/**
 * WP-H2.1 — demo seed only when probe explicitly says durable is OFF.
 * Avoids inventing LS demos before/while durable SoT is active (race with mount sync).
 */
export function shouldSeedPharmacyDemoData(): boolean {
  return getStorageItem(DURABLE_PROBE_KEY) === "0";
}

/** Reset probe cache (tests / after env change). */
export function resetDurableProbeCache(): void {
  durableConfiguredCache = null;
}
